import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// @mui
import {
  Box,
  Divider,
  MenuItem,
  Typography,
  useTheme,
  useMediaQuery,
  Dialog,
  Button,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
// routes
import { PATH_ADMIN, PATH_USER, PATH_KIOSK } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// utils
import { isHubApp } from '../../../utils/hubApp';
// components
import MenuPopover from '../../../components/MenuPopover';
import MyAvatar from '../../../components/MyAvatar';
import { IconButtonAnimate } from '../../../components/animate';

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(null);
  const [openDeactivateDialog, setOpenDeactivateDialog] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      if (user?.role === 'org') {
        navigate(PATH_ADMIN.login);
      } else if (user?.role === 'user') {
        navigate(PATH_USER.login);
      } else {
        navigate(PATH_KIOSK.login);
      }
      // navigate('/admin/login');

      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const handleSetting = () => {
    navigate(user?.role === 'org' ? PATH_ADMIN.profile : PATH_USER.settings);
    handleClose();
  };

  const handleOpenDeactivateDialog = () => {
    setOpenDeactivateDialog(true);
    handleClose();
  };

  const handleCloseDeactivateDialog = () => {
    setOpenDeactivateDialog(false);
  };

  const handleDeactivateAccount = async () => {
    try {
      enqueueSnackbar('Account deactivated successfully');

      // await logout();

      if (user?.role === 'org') {
        navigate(PATH_ADMIN.login);
      } else if (user?.role === 'user') {
        navigate(PATH_USER.login);
      } else {
        navigate(PATH_KIOSK.login);
      }

      handleCloseDeactivateDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to deactivate account', { variant: 'error' });
      handleCloseDeactivateDialog();
    }
  };

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpen}
        sx={{
          p: 0,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        <MyAvatar />
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.name}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem sx={{ m: 1 }} onClick={handleSetting}>
          Settings
        </MenuItem>

        <Divider sx={{ borderStyle: 'dashed' }} />

        {/* <MenuItem sx={{ m: 1 }} onClick={handleOrder}>
          My Orders
        </MenuItem> */}
        {user?.role === 'org' && (
          <MenuItem
            onClick={() => { navigate(PATH_ADMIN.documentation); handleClose(); }}
            sx={{ m: 1 }}
          >
            Documentation
          </MenuItem>
        )}
        {/* API keys and the access log: admins only, and never a nav entry. */}
        {user?.role === 'org' && hasPermission('developer.manage') && (
          <MenuItem
            onClick={() => { navigate(PATH_ADMIN.developer); handleClose(); }}
            sx={{ m: 1 }}
          >
            Developer Settings
          </MenuItem>
        )}
        {/* Hidden in the DeskOS Hub WebView until the billing pages work on a phone. */}
        {!isHubApp() && user?.role === 'org' && ['admin', 'accountant'].includes(user?.orgRole) && (
          <MenuItem
            onClick={() => { navigate(PATH_ADMIN.deskosBilling.root); handleClose(); }}
            sx={{ m: 1 }}
          >
            DeskOS Billing
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
          Logout
        </MenuItem>
        {/* <MenuItem
          onClick={handleOpenDeactivateDialog}
          sx={{
            m: 1,
            color: 'error.main',
          }}
        >
          Deactivate Account
        </MenuItem> */}
      </MenuPopover>

      <Dialog
        open={openDeactivateDialog}
        onClose={handleCloseDeactivateDialog}
        aria-labelledby="deactivate-account-dialog"
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: '400px',
            width: fullScreen ? 'calc(100% - 32px)' : '400px',
            mx: 'auto',
            my: 'auto',
          },
        }}
      >
        <Box sx={{ p: 3, pb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Deactivate Account
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
            Are you sure you want to deactivate your account? This action cannot be undone easily.
          </Typography>
        </Box>
        <Box
          sx={{
            p: 2,
            pt: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Button
            onClick={handleCloseDeactivateDialog}
            variant="outlined"
            sx={{
              borderRadius: 8,
              px: 3,
              color: 'text.primary',
              borderColor: 'divider',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeactivateAccount}
            variant="contained"
            sx={{
              bgcolor: 'error.main',
              borderRadius: 8,
              px: 3,
              '&:hover': {
                bgcolor: 'error.dark',
              },
            }}
          >
            Deactivate
          </Button>
        </Box>
      </Dialog>
    </>
  );
}
